import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';
import { toast } from '../utils/toast';
import { extractApiError } from '../utils/apiError';
import type {
  MailingList,
  MailingListDetailResponse,
  CreateMailingListResponse,
  CreateBroadcastResponse,
  PaginatedResponse,
  BroadcastEmail,
  CreateListPayload,
  RenameListPayload,
  CreateBroadcastPayload,
  AddContactPayload,
  BulkAddContactsPayload,
  UpdateContactPayload,
  ContactFilters,
  BroadcastFilters,
  InboxBroadcast,
} from '../components/mail/types';

// ─── Queries ──────────────────────────────────────────────────────────────────

/** List all mailing lists (paginated) — GET /mailing-lists/ */
export function useGetMailingLists(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['mailing-lists', page, pageSize],
    queryFn: async (): Promise<PaginatedResponse<MailingList>> => {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      const { data } = await apiClient.get(`/mailing-lists/?${params}`);
      return {
        count: data.count ?? 0,
        next: data.next ?? null,
        previous: data.previous ?? null,
        results: (data.results ?? []) as MailingList[],
      };
    },
  });
}

/** Single list detail + paginated contacts — GET /mailing-lists/{uuid}/ */
export function useGetMailingList(uuid: string | undefined, filters: ContactFilters = {}) {
  return useQuery({
    queryKey: ['mailing-list', uuid, filters],
    enabled: !!uuid,
    queryFn: async (): Promise<MailingListDetailResponse> => {
      const params = new URLSearchParams();
      if (filters.source) params.set('source', filters.source);
      if (filters.search) params.set('search', filters.search);
      if (filters.include_unsubscribed !== undefined) {
        params.set('include_unsubscribed', String(filters.include_unsubscribed));
      }
      params.set('page', String(filters.page ?? 1));
      params.set('page_size', String(filters.page_size ?? 20));

      const { data } = await apiClient.get(`/mailing-lists/${uuid}/?${params}`);
      return data;
    },
  });
}

/** Broadcast emails for a list — GET /mailing-lists/{uuid}/broadcast-emails/ */
export function useGetBroadcastEmails(
  listUuid: string | undefined,
  filters: BroadcastFilters = {},
) {
  return useQuery({
    queryKey: ['broadcast-emails', listUuid, filters],
    enabled: !!listUuid,
    queryFn: async (): Promise<PaginatedResponse<BroadcastEmail>> => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      params.set('page', String(filters.page ?? 1));
      params.set('page_size', String(filters.page_size ?? 20));

      const { data } = await apiClient.get(
        `/mailing-lists/${listUuid}/broadcast-emails/?${params}`,
      );
      return {
        count: data.count ?? 0,
        next: data.next ?? null,
        previous: data.previous ?? null,
        results: (data.results ?? []) as BroadcastEmail[],
      };
    },
  });
}

/** Organizer-level inbox screen — GET /mailing-lists/inbox/ */
export function useGetInboxBroadcasts(status: 'draft' | 'sent', page = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['inbox-broadcasts', status, page, pageSize],
    queryFn: async (): Promise<
      PaginatedResponse<InboxBroadcast> & { sent_count: number; draft_count: number }
    > => {
      const params = new URLSearchParams({
        status,
        page: String(page),
        page_size: String(pageSize),
      });
      const { data } = await apiClient.get(`/mailing-lists/inbox/?${params}`);
      return {
        count: data.count ?? 0,
        next: data.next ?? null,
        previous: data.previous ?? null,
        results: (data.results ?? []) as InboxBroadcast[],
        sent_count: data.sent_count ?? 0,
        draft_count: data.draft_count ?? 0,
        success: data.success ?? true,
      };
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** Retrieve full details of a specific broadcast email — GET /mailing-lists/{list_uuid}/broadcast-emails/{broadcast_uuid}/ */
export function useGetBroadcastEmail(
  listUuid: string | undefined,
  broadcastUuid: string | undefined,
) {
  return useQuery({
    queryKey: ['broadcast-email', listUuid, broadcastUuid],
    enabled: !!listUuid && !!broadcastUuid,
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
    queryFn: async (): Promise<{
      success: boolean;
      data: BroadcastEmail;
    }> => {
      const { data } = await apiClient.get(
        `/mailing-lists/${listUuid}/broadcast-emails/${broadcastUuid}/`,
      );
      return data;
    },
  });
}

/** Fetch raw base64 content for a broadcast's attachment — GET /mailing-lists/{list_uuid}/broadcast-emails/{broadcast_uuid}/attachment-content/ */
export function useGetBroadcastAttachmentContent(
  listUuid: string | undefined,
  broadcastUuid: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['broadcast-attachment-content', listUuid, broadcastUuid],
    enabled: !!listUuid && !!broadcastUuid && enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<{ success: boolean; content: string }> => {
      const { data } = await apiClient.get(
        `/mailing-lists/${listUuid}/broadcast-emails/${broadcastUuid}/attachment-content/`,
      );
      return data;
    },
  });
}

// ─── Mailing List Mutations ───────────────────────────────────────────────────

/** Create a new mailing list — POST /mailing-lists/ */
export function useCreateMailingList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateListPayload): Promise<CreateMailingListResponse> => {
      const { data } = await apiClient.post('/mailing-lists/', payload);
      return data;
    },
    onSuccess: () => {
      toast.success('Mailing list created');
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to create mailing list'));
    },
  });
}

/** Rename a mailing list — PATCH /mailing-lists/{uuid}/ */
export function useRenameMailingList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: RenameListPayload;
    }): Promise<CreateMailingListResponse> => {
      const { data } = await apiClient.patch(`/mailing-lists/${uuid}/`, payload);
      return data;
    },
    onSuccess: () => {
      toast.success('List renamed');
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
      queryClient.invalidateQueries({ queryKey: ['mailing-list'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to rename list'));
    },
  });
}

/** Delete a mailing list — DELETE /mailing-lists/{uuid}/ */
export function useDeleteMailingList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (uuid: string) => {
      await apiClient.delete(`/mailing-lists/${uuid}/`);
    },
    onSuccess: () => {
      toast.success('List deleted');
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to delete list'));
    },
  });
}

// ─── Contact Mutations ────────────────────────────────────────────────────────

/** Add contact(s) — POST /mailing-lists/{uuid}/contacts/ */
export function useAddContacts(listUuid: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddContactPayload | BulkAddContactsPayload) => {
      const { data } = await apiClient.post(`/mailing-lists/${listUuid}/contacts/`, payload);
      return data;
    },
    onSuccess: () => {
      toast.success('Contacts added');
      queryClient.invalidateQueries({ queryKey: ['mailing-list', listUuid] });
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to add contacts'));
    },
  });
}

/** Add contacts to a dynamically resolved mailing list UUID — POST /mailing-lists/{uuid}/contacts/ */
export function useAddContactsToMailingList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      listUuid,
      payload,
    }: {
      listUuid: string;
      payload: AddContactPayload | BulkAddContactsPayload;
    }) => {
      const { data } = await apiClient.post(`/mailing-lists/${listUuid}/contacts/`, payload);
      return data;
    },
    onSuccess: (_data, { listUuid }) => {
      toast.success('Contacts added');
      queryClient.invalidateQueries({ queryKey: ['mailing-list', listUuid] });
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to add contacts'));
    },
  });
}

/** Update a contact — PATCH /mailing-lists/{list_uuid}/contacts/{contact_uuid}/ */
export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      listUuid,
      contactUuid,
      payload,
    }: {
      listUuid: string;
      contactUuid: string;
      payload: UpdateContactPayload;
    }) => {
      const { data } = await apiClient.patch(
        `/mailing-lists/${listUuid}/contacts/${contactUuid}/`,
        payload,
      );
      return data;
    },
    onSuccess: (_data, { listUuid }) => {
      toast.success('Contact updated');
      queryClient.invalidateQueries({ queryKey: ['mailing-list', listUuid] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update contact'));
    },
  });
}

/** Delete a contact — DELETE /mailing-lists/{list_uuid}/contacts/{contact_uuid}/ */
export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ listUuid, contactUuid }: { listUuid: string; contactUuid: string }) => {
      await apiClient.delete(`/mailing-lists/${listUuid}/contacts/${contactUuid}/`);
    },
    onSuccess: (_data, { listUuid }) => {
      toast.success('Contact removed');
      queryClient.invalidateQueries({ queryKey: ['mailing-list', listUuid] });
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to remove contact'));
    },
  });
}

// ─── Broadcast Mutations ──────────────────────────────────────────────────────

/** Create a broadcast email — POST /mailing-lists/{uuid}/broadcast-emails/ */
export function useCreateBroadcast(listUuid: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateBroadcastPayload): Promise<CreateBroadcastResponse> => {
      const { data } = await apiClient.post(
        `/mailing-lists/${listUuid}/broadcast-emails/`,
        payload,
      );
      return data;
    },
    onSuccess: (response) => {
      if (response.cap_warning) {
        toast.warn(response.cap_warning);
      } else {
        toast.success(response.message || 'Broadcast sent');
      }
      queryClient.invalidateQueries({ queryKey: ['broadcast-emails', listUuid] });
      queryClient.invalidateQueries({ queryKey: ['inbox-broadcasts'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['inbox-broadcasts'] });
        queryClient.invalidateQueries({ queryKey: ['broadcast-emails', listUuid] });
      }, 1500);
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to send broadcast'));
    },
    networkMode: 'always',
  });
}

/** Update a broadcast email — PATCH /mailing-lists/{uuid}/broadcast-emails/{broadcast_uuid}/ */
export function useUpdateBroadcast(
  listUuid: string | undefined,
  broadcastUuid: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: Partial<CreateBroadcastPayload>,
    ): Promise<CreateBroadcastResponse> => {
      const { data } = await apiClient.patch(
        `/mailing-lists/${listUuid}/broadcast-emails/${broadcastUuid}/`,
        payload,
      );
      return data;
    },
    onSuccess: (response) => {
      if (response.cap_warning) {
        toast.warn(response.cap_warning);
      } else {
        toast.success(response.message || 'Broadcast updated');
      }
      queryClient.invalidateQueries({ queryKey: ['broadcast-emails', listUuid] });
      queryClient.invalidateQueries({ queryKey: ['broadcast-email', listUuid, broadcastUuid] });
      queryClient.invalidateQueries({ queryKey: ['inbox-broadcasts'] });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['inbox-broadcasts'] });
        queryClient.invalidateQueries({ queryKey: ['broadcast-emails', listUuid] });
      }, 1500);
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to update broadcast'));
    },
    networkMode: 'always',
  });
}

// ─── CSV Mutations ────────────────────────────────────────────────────────────

/** Export contacts as CSV — GET /mailing-lists/{uuid}/csv-export/ */
export function useCsvExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      listUuid,
      listName,
      filters,
    }: {
      listUuid: string;
      listName?: string;
      filters?: ContactFilters;
    }) => {
      const params = new URLSearchParams();
      if (filters?.source) params.set('source', filters.source);
      if (filters?.search) params.set('search', filters.search);
      if (filters?.include_unsubscribed !== undefined) {
        params.set('include_unsubscribed', String(filters.include_unsubscribed));
      }

      const response = await apiClient.get(`/mailing-lists/${listUuid}/csv-export/?${params}`, {
        responseType: 'blob',
      });

      const contentType = String(response.headers['content-type'] ?? '');
      if (contentType.includes('json') || response.data?.size === 0) {
        throw new Error('Export returned empty or invalid data.');
      }

      return { blob: response.data, listUuid, listName };
    },
    onSuccess: (data) => {
      const label = data.listName?.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase() || data.listUuid;
      const date = new Date().toISOString().split('T')[0];
      const filename = `${label}_${date}.csv`;

      const url = window.URL.createObjectURL(new Blob([data.blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Contacts exported successfully');
      queryClient.invalidateQueries({ queryKey: ['mailing-list', data.listUuid] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to export contacts'));
    },
  });
}

/** Import contacts from CSV — POST /mailing-lists/{uuid}/csv-import/ */
export function useCsvImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ listUuid, file }: { listUuid: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post(`/mailing-lists/${listUuid}/csv-import/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: (_data, { listUuid }) => {
      toast.success('Contacts imported');
      queryClient.invalidateQueries({ queryKey: ['mailing-list', listUuid] });
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
    },
    onError: (err: unknown) => {
      toast.error(extractApiError(err, 'Failed to import contacts'));
    },
  });
}
